/**
 * Checks if the given value is a valid date.
 *
 * @param value - The value to check. It can be a string or a Date object.
 * @returns A boolean indicating whether the value is a valid date.
 */
export function isDate(value: string | Date): boolean {
  const date = new Date(value);
  return !isNaN(date.getTime());
}

/**
 * Converts a Date object to a string representation in the format 'YYYY-MM-DD'.
 *
 * @param date - The Date object to convert.
 * @returns A string representation of the date in the format 'YYYY-MM-DD'.
 */
export function toYearMonthDayString(date: Date): string {
  return date.toISOString().split('T')[0];
}

/**
 * Converts a Date object to a string representation in the format 'YYYY-MM-DD' using local time. *
 * @param date - The Date object to convert.
 * @returns A string representation of the date in the format 'YYYY-MM-DD' in local timezone.
 */
export function toYearMonthDayLocalString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;  
}

/**
 * Converts a Date to time string in HH:mm:ss format (24-hour).
 * @param date - The Date object to convert.
 * @returns Time string in "HH:mm:ss" format.
 */
export function toTimeString(date: Date): string {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
}

/**
 * Parses a 24-hour time string into minutes since midnight.
 * @param time - Time string in "HH:mm" or "HH:mm:ss" format.
 * @returns Minutes since midnight, or NaN when the string is not a valid time.
 */
export function timeStringToMinutes(time: string | null | undefined): number {
  if (!time) return NaN;
  const [hoursStr, minutesStr] = time.split(':');
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return NaN;
  return hours * 60 + minutes;
}

/**
 * Formats minutes since midnight as a 24-hour "HH:mm" time string.
 * @param minutes - Minutes since midnight.
 * @returns Time string in "HH:mm" format.
 */
export function minutesToTimeString(minutes: number): string {
  const hours = Math.floor(minutes / 60).toString().padStart(2, '0');
  const mins = (minutes % 60).toString().padStart(2, '0');
  return `${hours}:${mins}`;
}

/**
 * Converts a Date to a short 12-hour clock string, e.g. "8:45 AM".
 * @param date - The Date (or ZonedDate) to convert.
 * @returns Time string in "h:mm AM/PM" format.
 */
export function toClockTimeString(date: Pick<Date, 'getHours' | 'getMinutes'>): string {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const meridiem = hours < 12 ? 'AM' : 'PM';
  const displayHours = hours % 12 === 0 ? 12 : hours % 12;
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${meridiem}`;
}

/**
 * Converts an ISO date string to a formatted month-year string.
 * @param isoDateString - The ISO date string to convert.
 * @returns The formatted month-year string.
 */
export function toMonthYearStringFromISOString(isoDateString: string): string {
  const date = new Date(isoDateString);
  const monthYear = date.toLocaleString("en-US", {
    month: "short",
    year: "2-digit",
  });

  const formattedDate = `${monthYear.split(" ")[0]} '${monthYear.split(" ")[1]}`;
  return formattedDate;
}

/**
 * Subtracts the given date in the past from the date in the future and returns the result as a new Date object.
 *
 * @param dateInFuture - The date in the future.
 * @param dateInPast - The date in the past.
 * @returns The difference between the two dates as a new Date object.
 */
export function subtractDate(dateInFuture: Date, dateInPast: Date): Date {
  return new Date(dateInFuture.getTime() - dateInPast.getTime());
}

/**
 * Returns a string representing the time elapsed between the given date and the current date.
 * The string is formatted as "{days} {daysString}, {hours} {hoursString} ago".
 *
 * @param date - The date to calculate the time elapsed from.
 * @returns A string representing the time elapsed.
 */
export function timeAgoString(date: Date): string {
  const now = new Date();
  const diffInMilliseconds = now.getTime() - date.getTime();
  const millisecondsInSecond = 1000;
  const millisecondsInMinute = millisecondsInSecond * 60;
  const millisecondsInHour = millisecondsInMinute * 60;
  const millisecondsInDay = millisecondsInHour * 24;

  const daysAgo = Math.floor(diffInMilliseconds / millisecondsInDay);
  const hoursAgo = Math.floor((diffInMilliseconds % millisecondsInDay) / millisecondsInHour);

  const daysString = daysAgo === 1 ? 'Day' : 'Days';
  const hoursString = hoursAgo === 1 ? 'Hour' : 'Hours';

  return `${daysAgo} ${daysString}, ${hoursAgo} ${hoursString} ago`;
}

/**
 * Converts a Date object to a formatted string representation.
 * @param date - The Date object to convert.
 * @returns A formatted string representation of the date.
 */
export function dateString(date: Date): string {
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  };

  return date.toLocaleDateString('en-US', options);
}

/**
 * Converts a date string to a formatted year-month-day string.
 * @param dateString - The date string to convert.
 * @param separator - The separator to use between year, month, and day. Defaults to "-".
 * @returns The formatted year-month-day string.
 */
export function toYearMonthDay(dateString: string, separator: string = "-"): string {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  return `${year}${separator}${month.toString().padStart(2, "0")}${separator}${day.toString().padStart(2, "0")}`;
}

/**
 * Converts a date string to a formatted string in the format "YYYY-MM-DD HH:mm AM/PM".
 * @param dateString - The date string to convert.
 * @param separator - The separator to use between year, month, and day. Default is "-".
 * @param space - The space to use between date and time. Default is " ".
 * @returns The formatted date string.
 */
export function toYearMonthDayTime(dateString: string, separator: string = "-", space: string = " "): string {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours() % 12 || 12; // Convert to 12-hour format
  const minutes = date.getMinutes();
  const ampm = date.getHours() >= 12 ? "PM" : "AM";

  return `${year}${separator}${month.toString()}${separator}${day.toString().padStart(2, "0")}${space}${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}${space}${ampm}`;
}

/**
 * Gets the expiry date of an invitation.
 * @param date  Date to check
 * @returns
 */
export function getExpiry(date: Date) {
  if (new Date(date) < new Date()) {
    return "Expired";
  } else {
    const diff = Math.abs(new Date(date).getTime() - new Date().getTime());
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    return `${days} Days ${hours} Hours`;
  }
};

/**
 * Converts a 24-hour time string to a 12-hour time string with AM/PM.
 * @param time24h - The 24-hour time string (e.g., "17:00").
 * @returns The 12-hour time string with AM/PM (e.g., "5:00 PM").
 */
export function to12HourTime(time24h: string | null | undefined): string {
  if (!time24h) return '';
  const [hoursStr, minutes] = time24h.split(':');
  let hours = parseInt(hoursStr, 10);
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;

  return `${hours}:${minutes} ${ampm}`;
}

/**
 * Parses a date string into a Date object in local timezone, avoiding UTC conversion issues.
 * Optionally accepts a time string to set the time component.
 * @param dateStr - The date string to parse in format "YYYY-MM-DD".
 * @param timeStr - Optional time string in format "HH:mm:ss" or "HH:mm".
 * @returns A Date object in local timezone, set to midnight if no time is provided.
 */
export function parseLocalDate(dateStr: string, timeStr?: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  if (timeStr) {
    const [hours, minutes, seconds = 0] = timeStr.split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes, seconds);
  }
  return new Date(year, month - 1, day);
}

/**
 * Gets the current date at midnight (00:00:00) in local timezone.
 * @returns A Date object representing today at midnight.
 */
export function getTodayAtMidnight(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

export function formatDate(dateString: string): string {
  if (!dateString) return '';
  let date: Date;
    // YYYY-MM-DD
    const [year, month, day] = dateString.split('-').map(Number);
    date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};
/**
 * Formats a date to short US format (e.g., "Dec 25, 2024").
 * @param date - Date object or string to format.
 * @returns Formatted date string.
 */
export function formatDateShort(date: Date | string): string {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Formats a date to long US format (e.g., "December 25, 2024").
 * @param date - Date object or string to format.
 * @returns Formatted date string.
 */
export function formatDateLong(date: Date | string): string {
  const d = date instanceof Date ? date : convertStringDateToObjDate(date);
  if (!d) return '';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function formatDateToLongUsWeekday(dateString?: string | Date | null){
  if (!dateString) return null;
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  // Ex. Wednesday, December 25, 2025
  return date.toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'long', 
    day: 'numeric',
    year: 'numeric'
  });
};

export function formatDateToLongUsWeekdayLocal(dateString?: string | null){
  if (!dateString) return null;
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(dateString) ? `${dateString}T00:00:00` : dateString;
  const date = new Date(normalized);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

export function formatTimeOnly(date: Date | null): string | null {
  if (!date) return null;
  return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
};

export function timeStringToDate(timeString: string, date: Date): Date {
  const [hours, minutes] = timeString.split(':').map(Number);
  const newDate = new Date(date);
  newDate.setHours(hours, minutes, 0, 0);
  return newDate;
};

/**
 * Calculates the start and end dates of the week based on a given date.
 * The week is assumed to start on Sunday and end on Saturday.
 *
 * @param date - A `Date` object representing the selected date.
 * @returns An object containing:
 *   - `start`: A `Date` object for the start of the week (Sunday).
 *   - `end`: A `Date` object for the end of the week (Saturday).
 */
export function getWeekRange(date: Date): {start: Date; end: Date; }
{
  const dateTime = new Date(date.getTime());
  const dayOfWeek = dateTime.getUTCDay();
  const weekStart = new Date(dateTime.getTime() - dayOfWeek * 24 * 60 * 60 * 1000);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);

  return {
      start: weekStart,
      end: weekEnd,
    }
  ;
}

/**
 * Calculates the number of days between two dates (inclusive).
 * @param start - Start date.
 * @param end - End date.
 * @returns Number of days.
 */
export function getDaysBetween(start: Date | string, end: Date | string): number {
  const startDate = start instanceof Date ? start : new Date(start);
  const endDate = end instanceof Date ? end : new Date(end);
  return Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
}

/**
 * Gets month abbreviation and day number from a date.
 * @param date - Date to extract from.
 * @returns Object with month (uppercase abbreviation) and day (number).
 */
export function getMonthDayDisplay(date: Date | string): { month: string; day: number } {
  const d = date instanceof Date ? date : convertStringDateToObjDate(date);
  if (!d) return { month: '', day: 0 };
  return {
    month: d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
    day: d.getDate()
  };
}

/**
 * Converts a date string to a JavaScript Date object
 * Handles ISO format dates (YYYY-MM-DD) by parsing components to avoid timezone issues
 * @returns The DateOnly.
*/
export const convertStringDateToObjDate = (dateStr: any): Date | null => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;
  
  if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
    // Handle ISO format dates (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss)
    const [year, month, day] = dateStr.split('T')[0].split('-');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  } else if (typeof dateStr === 'string') {
    return new Date(dateStr);
  }
  
  return null;
};

/**
 * Formats a Date object for use in datetime-local input fields.
 * Returns the date in "YYYY-MM-DDTHH:mm" format in local timezone.
 * @param date - The Date object to format.
 * @returns A string in "YYYY-MM-DDTHH:mm" format, or empty string if invalid.
 */
export function formatDateTimeLocal(date: Date): string {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "";
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

